import { Controller } from "@hotwired/stimulus"
import { loadMapboxGL } from "../lib/mapbox_loader"

// Mapbox is lazy-loaded from CDN to reduce bundle size
export default class extends Controller {
  static targets = ["container", "zipCodes"]

  connect() {
    this.map = null
    this.mapInitialized = false
    this.mapboxgl = null

    // Listen for the toggle event to initialize map when visible
    this.element.addEventListener('zone-type:zip-shown', () => {
      this.ensureMapInitialized()
    })

    // Check if already visible
    if (!this.element.classList.contains('hidden')) {
      this.ensureMapInitialized()
    }

    // Listen for zip code input changes
    if (this.hasZipCodesTarget) {
      this.zipCodesTarget.addEventListener('input', this.debounce(() => this.updateZipBoundaries(), 500))
    }
  }

  get brandColor() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--brand-500').trim() || '#3b82f6'
  }

  ensureMapInitialized() {
    if (this.mapInitialized) {
      if (this.map) {
        this.map.resize()
      }
      return
    }

    setTimeout(() => this.initializeMap(), 50)
  }

  async initializeMap() {
    if (!this.hasContainerTarget) return
    if (this.mapInitialized) return

    const rect = this.containerTarget.getBoundingClientRect()
    if (rect.width === 0 || rect.height === 0) {
      setTimeout(() => this.initializeMap(), 100)
      return
    }

    // Load mapbox lazily
    this.mapboxgl = await loadMapboxGL()
    this.mapboxgl.accessToken = 'pk.eyJ1IjoiYWxldmVyZW56NTgiLCJhIjoiY21pZGozaTYwMDczZzJrb295bnhkY3JuOCJ9._JeXNOLxl5PhHINW4eIXUw'

    // Default to US center
    this.map = new this.mapboxgl.Map({
      container: this.containerTarget,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [-98.5795, 39.8283],
      zoom: 4,
      pitch: 0,
      bearing: 0,
      antialias: true
    })

    this.mapInitialized = true

    this.map.addControl(new this.mapboxgl.NavigationControl(), 'top-right')

    this.map.on('load', () => {
      // Add source for zip boundaries
      this.map.addSource('zip-boundaries', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] }
      })

      this.map.addLayer({
        id: 'zip-boundaries-fill',
        type: 'fill',
        source: 'zip-boundaries',
        paint: {
          'fill-color': this.brandColor,
          'fill-opacity': 0.2
        }
      })

      this.map.addLayer({
        id: 'zip-boundaries-stroke',
        type: 'line',
        source: 'zip-boundaries',
        paint: {
          'line-color': this.brandColor,
          'line-width': 2
        }
      })

      // Load initial zip codes if any
      this.updateZipBoundaries()
    })
  }

  async updateZipBoundaries() {
    if (!this.map || !this.hasZipCodesTarget) return

    const zipInput = this.zipCodesTarget.value
    const zipCodes = zipInput
      .split(/[,\n\s]+/)
      .map(z => z.trim())
      .filter(z => /^\d{5}$/.test(z))

    if (zipCodes.length === 0) {
      this.map.getSource('zip-boundaries')?.setData({ type: 'FeatureCollection', features: [] })
      return
    }

    const features = []
    const bounds = new this.mapboxgl.LngLatBounds()

    for (const zip of zipCodes) {
      try {
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${zip}.json?country=US&types=postcode&access_token=${this.mapboxgl.accessToken}`
        )
        const data = await response.json()

        if (data.features && data.features.length > 0) {
          const feature = data.features[0]
          const [lng, lat] = feature.center

          // Create approximate coverage area (~5 mile radius)
          const circle = this.createGeoJSONCircle([lng, lat], 8046.72)
          circle.properties = { zipCode: zip }
          features.push(circle)

          bounds.extend([lng, lat])
        }
      } catch (error) {
        console.error(`Error fetching zip ${zip}:`, error)
      }
    }

    if (features.length > 0) {
      this.map.getSource('zip-boundaries').setData({
        type: 'FeatureCollection',
        features: features
      })

      this.map.fitBounds(bounds, { padding: 50, maxZoom: 12 })
    }
  }

  createGeoJSONCircle(center, radiusMeters, points = 64) {
    const coords = { latitude: center[1], longitude: center[0] }
    const km = radiusMeters / 1000
    const ret = []
    const distanceX = km / (111.320 * Math.cos(coords.latitude * Math.PI / 180))
    const distanceY = km / 110.574

    for (let i = 0; i < points; i++) {
      const theta = (i / points) * (2 * Math.PI)
      const x = distanceX * Math.cos(theta)
      const y = distanceY * Math.sin(theta)
      ret.push([coords.longitude + x, coords.latitude + y])
    }
    ret.push(ret[0])

    return {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [ret]
      },
      properties: {}
    }
  }

  debounce(func, wait) {
    let timeout
    return (...args) => {
      clearTimeout(timeout)
      timeout = setTimeout(() => func.apply(this, args), wait)
    }
  }

  disconnect() {
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  }
}
